import type {
  QuestionnaireBrandingColorField,
  QuestionnaireBrandingImageField,
  QuestionnaireElementDefinition,
  QuestionnaireElementKind,
} from '@common/types';

export const QUESTIONNAIRE_ELEMENT_CATALOG: ReadonlyArray<QuestionnaireElementDefinition> = [
  {
    key: 'radio',
    kind: 'radio',
    group: 'question',
    label: 'Radio Buttons',
    icon: 'radio_button_checked',
    description: 'Pick one option from a list.',
  },
  {
    key: 'radio-grid',
    kind: 'radio-grid',
    group: 'question',
    label: 'Radio Button Grid',
    icon: 'grid_on',
    description: 'Rate several items on one scale.',
  },
  {
    key: 'dropdown',
    kind: 'dropdown',
    group: 'question',
    label: 'Dropdown Menu',
    icon: 'arrow_drop_down_circle',
    description: 'Pick one option from a menu.',
  },
  {
    key: 'dropdown-grid',
    kind: 'dropdown-grid',
    group: 'question',
    label: 'Dropdown Grid',
    icon: 'grid_view',
    description: 'A dropdown for each item in a list.',
  },
  {
    key: 'checkbox',
    kind: 'checkbox',
    group: 'question',
    label: 'Checkboxes',
    icon: 'check_box',
    description: 'Pick any number of options.',
  },
  {
    key: 'checkbox-grid',
    kind: 'checkbox-grid',
    group: 'question',
    label: 'Checkbox Grid',
    icon: 'apps',
    description: 'Tick options across several items.',
  },
  {
    key: 'multi-select',
    kind: 'multi-select',
    group: 'question',
    label: 'Multi-Select Dropdown',
    icon: 'checklist',
    description: 'Pick several options from a menu.',
  },
  {
    key: 'textbox',
    kind: 'textbox',
    group: 'question',
    label: 'Textbox',
    icon: 'short_text',
    description: 'A single line of free text.',
  },
  {
    key: 'textbox-grid',
    kind: 'textbox-grid',
    group: 'question',
    label: 'Textbox Grid',
    icon: 'table_rows',
    description: 'A text field for each item in a list.',
  },
  {
    key: 'long-answer',
    kind: 'long-answer',
    group: 'question',
    label: 'Long Answer',
    icon: 'notes',
    description: 'A multi-line comment box.',
  },
  {
    key: 'hidden-parameter',
    kind: 'hidden-parameter',
    group: 'question',
    label: 'Hidden Parameter',
    icon: 'visibility_off',
    description: 'Capture a value without showing it to the guest.',
  },
  {
    key: 'image',
    kind: 'image',
    group: 'content',
    label: 'Image',
    icon: 'image',
    description: 'Show a picture.',
  },
  {
    key: 'text',
    kind: 'text',
    group: 'content',
    label: 'Text',
    icon: 'subject',
    description: 'A paragraph of copy.',
  },
  {
    key: 'html',
    kind: 'html',
    group: 'content',
    label: 'HTML',
    icon: 'code',
    description: 'Custom markup for advanced layouts.',
  },
  {
    key: 'horizontal-rule',
    kind: 'horizontal-rule',
    group: 'content',
    label: 'Horizontal Rule',
    icon: 'horizontal_rule',
    description: 'A divider between sections.',
  },
  {
    key: 'headline-1',
    kind: 'headline',
    group: 'content',
    label: 'Headline 1',
    icon: 'title',
    description: 'Largest section heading.',
    headlineLevel: 1,
  },
  {
    key: 'headline-2',
    kind: 'headline',
    group: 'content',
    label: 'Headline 2',
    icon: 'title',
    description: 'Section heading.',
    headlineLevel: 2,
  },
  {
    key: 'headline-3',
    kind: 'headline',
    group: 'content',
    label: 'Headline 3',
    icon: 'title',
    description: 'Sub-section heading.',
    headlineLevel: 3,
  },
  {
    key: 'headline-4',
    kind: 'headline',
    group: 'content',
    label: 'Headline 4',
    icon: 'title',
    description: 'Minor heading.',
    headlineLevel: 4,
  },
  {
    key: 'headline-5',
    kind: 'headline',
    group: 'content',
    label: 'Headline 5',
    icon: 'title',
    description: 'Small heading.',
    headlineLevel: 5,
  },
  {
    key: 'headline-6',
    kind: 'headline',
    group: 'content',
    label: 'Headline 6',
    icon: 'title',
    description: 'Smallest heading.',
    headlineLevel: 6,
  },
];

export const QUESTIONNAIRE_CHOICE_KINDS: ReadonlyArray<QuestionnaireElementKind> = [
  'radio',
  'dropdown',
  'checkbox',
  'multi-select',
  'radio-grid',
  'dropdown-grid',
  'checkbox-grid',
];

export const QUESTIONNAIRE_GRID_KINDS: ReadonlyArray<QuestionnaireElementKind> = [
  'radio-grid',
  'dropdown-grid',
  'checkbox-grid',
  'textbox-grid',
];

export const QUESTIONNAIRE_PLACEHOLDER_KINDS: ReadonlyArray<QuestionnaireElementKind> = [
  'textbox',
  'long-answer',
  'dropdown',
  'multi-select',
];

export const QUESTIONNAIRE_CONTENT_KINDS: ReadonlyArray<QuestionnaireElementKind> = ['text', 'html'];

export const QUESTIONNAIRE_BRANDING_COLOR_FIELDS: ReadonlyArray<QuestionnaireBrandingColorField> = [
  {
    key: 'foregroundColor',
    label: 'Background',
    description: 'The surface the questionnaire sits on.',
  },
  { key: 'textColor', label: 'Text', description: 'Questions, help text and answers.' },
  { key: 'buttonColor', label: 'Buttons', description: 'Back, next and submit buttons.' },
  { key: 'borderColor', label: 'Borders', description: 'Field outlines, dividers and grid lines.' },
  { key: 'linkColor', label: 'Links', description: 'Anything clickable inside your copy.' },
];

export const QUESTIONNAIRE_BRANDING_IMAGE_FIELDS: ReadonlyArray<QuestionnaireBrandingImageField> = [
  {
    key: 'headerImageUrl',
    label: 'Header image',
    description: 'Sits above the first question — usually your logo.',
    icon: 'vertical_align_top',
    placeholder: '/assets/images/logo.png',
  },
  {
    key: 'footerImageUrl',
    label: 'Footer image',
    description: 'Sits below the buttons — a sign-off or brand strip.',
    icon: 'vertical_align_bottom',
    placeholder: '/assets/images/footer.png',
  },
];

export const QUESTIONNAIRE_EMAIL_LANGUAGES: ReadonlyArray<string> = [
  'English',
  'Albanian',
  'Afrikaans',
  'Dutch',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Spanish',
];
